import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { tradingApi } from '../services/tradingApi';
import type { PermissionKey } from '../types/permissions';

interface PermissionContextValue {
  permissions: Set<PermissionKey>;
  roles: string[];
  loading: boolean;
  hasPermission: (key: PermissionKey) => boolean;
  hasAnyPermission: (keys: PermissionKey[]) => boolean;
  refetch: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextValue | undefined>(undefined);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState<Set<PermissionKey>>(new Set());
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated) {
      setPermissions(new Set());
      setRoles([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await tradingApi.getMyPermissions();
      setPermissions(new Set(data.permissions));
      setRoles(data.roles);
    } catch (err) {
      console.error('[PermissionContext] Failed to fetch permissions:', err);
      setPermissions(new Set());
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasPermission = useCallback(
    (key: PermissionKey) => permissions.has(key),
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (keys: PermissionKey[]) => keys.some(k => permissions.has(k)),
    [permissions]
  );

  return (
    <PermissionContext.Provider
      value={{ permissions, roles, loading, hasPermission, hasAnyPermission, refetch: fetchPermissions }}
    >
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissionContext(): PermissionContextValue {
  const ctx = useContext(PermissionContext);
  if (ctx === undefined) {
    throw new Error('usePermissionContext must be used within a PermissionProvider');
  }
  return ctx;
}
